import React, { createContext, useContext, useState, useCallback } from 'react';

const AssessmentContext = createContext(null);

export const useAssessment = () => {
  const context = useContext(AssessmentContext);
  if (!context) {
    throw new Error('useAssessment must be used within AssessmentProvider');
  }
  return context;
};

const INITIAL_STATE = {
  currentStep: 0,
  businessProfile: {
    business_name: '',
    business_status: ''
  },
  loanProfile: {
    loan_amount_band: '',
    funding_purposes: []
  },
  path: null,
  responses: {},
  checklistProgress: {}
};

export const AssessmentProvider = ({ children }) => {
  const [state, setState] = useState(INITIAL_STATE);

  const setBusinessProfile = useCallback((profile) => {
    setState(prev => ({
      ...prev,
      businessProfile: { ...prev.businessProfile, ...profile }
    }));
  }, []);

  const setLoanProfile = useCallback((profile) => {
    setState(prev => ({
      ...prev,
      loanProfile: { ...prev.loanProfile, ...profile }
    }));
  }, []);

  const toggleFundingPurpose = useCallback((purpose) => {
    setState(prev => {
      const currentPurposes = prev.loanProfile.funding_purposes || [];
      const newPurposes = currentPurposes.includes(purpose)
        ? currentPurposes.filter(p => p !== purpose)
        : [...currentPurposes, purpose];
      return {
        ...prev,
        loanProfile: { ...prev.loanProfile, funding_purposes: newPurposes }
      };
    });
  }, []);

  const setPath = useCallback((path) => {
    setState(prev => ({ ...prev, path }));
  }, []);

  const setResponse = useCallback((questionId, answer, score) => {
    setState(prev => ({
      ...prev,
      responses: {
        ...prev.responses,
        [questionId]: { question_id: questionId, answer, score }
      }
    }));
  }, []);

  const setChecklistItem = useCallback((itemId, completed) => {
    setState(prev => ({
      ...prev,
      checklistProgress: {
        ...prev.checklistProgress,
        [itemId]: completed
      }
    }));
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
  }, []);

  const prevStep = useCallback(() => {
    setState(prev => ({ ...prev, currentStep: Math.max(0, prev.currentStep - 1) }));
  }, []);

  const goToStep = useCallback((step) => {
    setState(prev => ({ ...prev, currentStep: step }));
  }, []);

  const resetAssessment = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const getResponsesList = useCallback(() => {
    return Object.values(state.responses);
  }, [state.responses]);

  const value = {
    ...state,
    setBusinessProfile,
    setLoanProfile,
    toggleFundingPurpose,
    setPath,
    setResponse,
    setChecklistItem,
    nextStep,
    prevStep,
    goToStep,
    resetAssessment,
    getResponsesList
  };

  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  );
};

export default AssessmentContext;
